import { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';

const WebSocketContext = createContext(null);

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 30000;

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const listenersRef = useRef(new Map());
  const [connected, setConnected] = useState(false);

  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const protocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const host = backendUrl.replace(/^https?:\/\//, '');
    const token = localStorage.getItem('cit_token');
    if (!token) return null;
    return `${protocol}://${host}/api/ws?token=${token}`;
  }, []);

  const connect = useCallback(() => {
    const url = getWsUrl();
    if (!url || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        // Start ping interval
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === 'pong') return;
          // Notify all listeners registered for this event
          const eventListeners = listenersRef.current.get(msg.event) || [];
          eventListeners.forEach(cb => cb(msg.data));
          // Also notify wildcard listeners (listen to all events)
          const wildcardListeners = listenersRef.current.get('*') || [];
          wildcardListeners.forEach(cb => cb(msg.event, msg.data));
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingRef.current);
        // Reconnect if user is still logged in
        if (localStorage.getItem('cit_token')) {
          reconnectRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      // WebSocket creation failed, retry
      reconnectRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
    }
  }, [getWsUrl]);

  const disconnect = useCallback(() => {
    clearInterval(pingRef.current);
    clearTimeout(reconnectRef.current);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  // Subscribe to a specific event type. Returns unsubscribe function.
  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, []);
    }
    listenersRef.current.get(eventType).push(callback);

    return () => {
      const list = listenersRef.current.get(eventType) || [];
      listenersRef.current.set(eventType, list.filter(cb => cb !== callback));
    };
  }, []);

  // Connect on mount if token exists, and poll for token availability
  useEffect(() => {
    if (localStorage.getItem('cit_token')) {
      connect();
    }
    // Also check periodically for token appearing after login
    const checkToken = setInterval(() => {
      const hasToken = !!localStorage.getItem('cit_token');
      const isConnected = wsRef.current?.readyState === WebSocket.OPEN;
      if (hasToken && !isConnected) {
        connect();
      } else if (!hasToken && isConnected) {
        disconnect();
      }
    }, 2000);
    return () => { clearInterval(checkToken); disconnect(); };
  }, [connect, disconnect]);

  return (
    <WebSocketContext.Provider value={{ connected, subscribe, connect, disconnect }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);

/**
 * Hook to subscribe to specific WS events and trigger a refetch callback.
 * Usage: useWsRefetch(['deal_created', 'deal_updated'], fetchDeals);
 */
export const useWsRefetch = (events, refetchFn) => {
  const ws = useWebSocket();
  
  useEffect(() => {
    if (!ws) return;
    const unsubs = events.map(evt => ws.subscribe(evt, () => {
      refetchFn();
    }));
    return () => unsubs.forEach(unsub => unsub());
  }, [ws, events, refetchFn]);
};
